import { Heading, P } from '@slal/ui/typography';
import { useEffect } from 'react';
import { MaxWidthContent, Notice, Notification, ValueSummary } from '~/components';
import { useApplicationContext, useInvestmentPathwayCharges } from '~/hooks';
import { InvestmentPathwayOption } from '~/types';
import { InvestmentPathwayFactsheet } from './InvestmentPathwayFactsheet';
import { InvestmentPathwaySummary } from './InvestmentPathwaySummary';
import {
    RoundelEquals,
    RoundelMinus,
    StyledInvestmentChargesWrapper,
    StyledInvestmentPathwayBreakdown,
} from './styles';
import { InvestmentPathwayProps } from './types';

export const InvestmentPathwayCharges = ({ fundCode, onErrorChange }: InvestmentPathwayProps) => {
    const { discount, loading, totalCharge, totalExpenseRate, error } = useInvestmentPathwayCharges(fundCode);

    useEffect(() => {
        if (!loading) {
            onErrorChange?.(!!error);
        }
    }, [loading, error, onErrorChange]);

    if (!fundCode) return null;

    if (loading) return null;

    if (error) {
        console.error('InvestmentPathwayCharges error:', error);

        return (
            <Notice level="danger">
                We were unable to retrieve the Investment Pathways Charges right now. Please try again later to
                continue.
            </Notice>
        );
    }

    return (
        <StyledInvestmentChargesWrapper>
            <ValueSummary
                title="Charges"
                value={`${totalExpenseRate}%`}
                summary="This is made up of the Fund Management Charge and any Additional Expenses."
                loading={loading}
            />
            <RoundelMinus>-</RoundelMinus>
            <ValueSummary
                title="Discount"
                value={`${discount}%`}
                summary="This will vary depending on your pension value (0.3% discount for less than £25,000 or 0.5% discount for £25,000 or more)."
            />
            <RoundelEquals>=</RoundelEquals>
            <ValueSummary
                title="What you pay"
                value={`${totalCharge}%`}
                summary="This is the effective total annual fund charge you'd pay if your plan was invested in this investment option."
                loading={loading}
            />
        </StyledInvestmentChargesWrapper>
    );
};

export const InvestmentPathwayContradictoryWarning = ({ fundCode }: InvestmentPathwayProps) => {
    const { withdrawalOption } = useApplicationContext();

    if (!fundCode) return null;

    if (withdrawalOption === 'Lump sum') return null;

    if (fundCode === InvestmentPathwayOption.OPTION_1) {
        return (
            <Notification level="warning">
                <P>
                    While you can take money from your pension whenever you want, we don't want you to be in an
                    investment that might not be right for you. You have chosen to take a regular income from your
                    pension pot but you've picked an investment option that is designed for people who are not taking
                    any money in the next 5 years, so please consider if this is the right option for you.{' '}
                </P>
            </Notification>
        );
    }

    if (fundCode === InvestmentPathwayOption.OPTION_2) {
        return (
            <Notification level="warning">
                <P>
                    While you can take money from your pension whenever you want, we don't want you to be in an
                    investment that might not be right for you. If you choose this investment option and take a regular
                    income from your pension pot, you could have significantly less to buy an annuity with in the
                    future. Please consider this when deciding to take money and making your investment choices.
                </P>
            </Notification>
        );
    }

    return null;
};
export const InvestmentPathwayBreakdown = ({ fundCode, onErrorChange }: InvestmentPathwayProps) => {
    if (!fundCode) return null;
    return (
        <>
            <Heading level={3}>Based on your selection:</Heading>
            <StyledInvestmentPathwayBreakdown>
                <MaxWidthContent>
                    <InvestmentPathwaySummary fundCode={fundCode} />
                    <InvestmentPathwayFactsheet fundCode={fundCode} />
                </MaxWidthContent>
                <Heading level={4}>What are the total charges?</Heading>
                <InvestmentPathwayCharges fundCode={fundCode} onErrorChange={onErrorChange} />
            </StyledInvestmentPathwayBreakdown>
        </>
    );
};
